import { LitElement, html } from 'lit';

class CollectionCard extends LitElement {
  static properties = {
    storageKey: { type: String, attribute: 'storage_key' },
    products:          { type: Array },
    selected_variants: { type: Object },
    collection:        { type: String, reflect: true },
    columnsDesktop: { type: String, attribute: 'columns-desktop' },
    cardGap: { type: String, attribute: 'card-gap' },
    sizeStyle:         { type: String, attribute: 'size-style' },
    enableLoadMore: { type: String, attribute: 'enable-load-more' },
    initialCount:   { type: Number, attribute: 'initial-count' },
    loadingProductId: { type: Number },
    currencySymbol: { type: String, attribute: 'currency-symbol' },
    currencyCode: { type: String, attribute: 'currency-code' },
    

  };

  constructor() {
    super();
    this.products          = [];
    this.selected_variants = {};
    this.cardGap = 'default';
    this.activeOverlayId = null;
    this.sizeStyle = 'white';
    this.enableLoadMore = false;
    this.initialCount = 0;
    this.displayedProducts = [];
    this.showCount = 0;
    this.columnsDesktop = '5';
    this.loadingProductId = null;
    this.currencySymbol = '$';
    this.currencyCode = 'AUD';
    this.dataLoaded = false;

  }

  createRenderRoot() { return this; }

  updated(changed) {
    if (changed.has('collection')) {
      this.products = [];
      this.loadProductsFromStorage();
    }
  }
  
  firstUpdated() {
    this.loadProductsFromStorage();

    const eventName = `${this.storageKey}_ready`;
    document.addEventListener(eventName, () => {
      this.loadProductsFromStorage();
    }, { once: true });
  }
    

  loadProductsFromStorage() {
    const data = localStorageGetItemWithExpiry(this.storageKey);

  
    if (!data) {
      console.warn('No products found in localStorage');
      return;
    }
  
    const products = this.collection === 'search_results'
    ? JSON.parse(localStorage.getItem(`${this.storageKey}_search_results`)) || []
    : data[this.collection] || [];
  
  
  
    this.products = products.map(p => ({
      ...p,
      sizes: [...new Set((p.variants || []).map(v => v.title.split('/')[0]))]
    }));

    if (this.enableLoadMore === 'enabled') {
      this.showCount = this.initialCount;
      this.displayedProducts = this.products.slice(0, this.showCount);
    } else {
      this.displayedProducts = [...this.products];
    }    
  
    this.dataLoaded = true;
    this.requestUpdate();
  }
  

  async handleSizeClick(id, size) {
    const p = this.products.find(p => p.id === id);
    if (!p) return;
    const v = p.variants.find(v => v.title.split('/')[0] === size);
    if (!v || !v.available) return;
  
    this.loadingProductId = id;
    this.requestUpdate();
  
    try {
      if (this.storageKey === 'outfit_products') {
        document.querySelector('outfit-build')?.addItem({ variantId: v.id, image: p.ghost_img});
      } else {
        await window.cartUtils.addItem(v.id, 1);
        window.drawerFunctions.toggleDrawer('drawer_sidecart', true);
      }
      this.selected_variants[id] = v.id;
    } catch (err) {
      console.error('Error adding to cart:', err);
    }
  
    this.loadingProductId = null;
    this.requestUpdate();
  }
  

  toggleOverlay(productId) {
    this.activeOverlayId = this.activeOverlayId === productId ? null : productId;
    this.requestUpdate();
  }
  
  loadMoreProducts(e) {
    const nextCount = this.showCount + this.initialCount;
    this.showCount = nextCount;
    this.displayedProducts = this.products.slice(0, nextCount);
    e.target.blur();
    this.requestUpdate();
  }  
  

  render() {
    const isLoading = !this.dataLoaded;
    const hasNoResults = this.dataLoaded && this.displayedProducts.length === 0;
       
    return html`
    <div class="collection_cards ${this.cardGap === 'none' ? 'no_gap' : 'default_gap'} columns-${this.columnsDesktop}">
    ${isLoading
      ? html`
          <div class="initial_collection_spinner" role="status" aria-label="Loading products...">
            <img
              src=${theme.images.loading}
              alt="Loading..."
              class="loading_gif"
            />
          </div>
        `
      : hasNoResults
      ? html`
          <div class="no_results_message">
            <p>No products match this filter.</p>
          </div>
        `
      : ''}    
       ${this.displayedProducts.map((p, i) => html`
          <div class="collection_card" style="animation-delay: ${i * 0.08}s;">
            <div class="collection_card_image_wrapper">
            <a href="/products/${p.handle}" aria-label="${p.title}">
              <img src="${this.storageKey === 'outfit_products' ? p.ghost_img : p.image}" alt="${p.title}">
            </a>
             <div class="collection_card_size_overlay ${this.sizeStyle} ${this.activeOverlayId === p.id ? 'visible' : ''}">
                ${this.loadingProductId === p.id
                  ? html`<div class="collection_card_spinner white"></div>`
                  : html`
                      ${p.sizes.map(sz => {
                        const v = p.variants.find(v => v.title.split('/')[0] === sz);
                        if (!v) return null;

                        return !v.available
                          ? html`<a
                              href="${v.variant_url}"
                              class="collection_card_size_option sold_out"
                              aria-label="Sold out – ${p.title} ${sz}"
                            >${sz}</a>`
                          : html`<span
                              class="collection_card_size_option"
                              @click=${() => this.handleSizeClick(p.id, sz)}
                            >${sz}</span>`;
                      })}
                    `
                }
              </div>
              <button class="mob_quick_add_btn" @click=${() => this.toggleOverlay(p.id)}>
                + Quick add
              </button>
            </div>
            <div class="collection_card_info">
              <div class="collection_card_title">
                <a href="/products/${p.handle}" aria-label="${p.title}">${p.title}</a>
              </div>
                <div class="collection_card price">
                  ${p.compare_at_price && parseFloat(p.compare_at_price) > parseFloat(p.price)
                    ? html`
                        <span class="original_price">
                          ${this.currencySymbol}${parseFloat(p.compare_at_price).toFixed(2)}
                        </span>
                        <span class="sale_price_group">
                          ${this.currencySymbol}${parseFloat(p.price).toFixed(2)} ${this.currencyCode}
                          <span class="discount">
                            ${Math.round((1 - parseFloat(p.price) / parseFloat(p.compare_at_price)) * 100)}% OFF
                          </span>
                        </span>
                      `
                    : html`
                        <span class="regular_price">
                          ${this.currencySymbol}${parseFloat(p.price).toFixed(2)} ${this.currencyCode}
                        </span>
                      `
                  }
                </div>
              </div>
            </div>
          </div> 
        `)}
      </div>
      ${this.enableLoadMore === 'enabled' && this.displayedProducts.length < this.products.length ? html`
        <div class="load_more_container">
          <button class="load_more_btn btn btn-primary" @click=${this.loadMoreProducts.bind(this)}>
            Load More
          </button>
        </div>
      ` : ''}        
    `;
  }
}

customElements.define('collection-card', CollectionCard);




